import { useEffect, useState, useMemo } from "react";
import { api } from "@/api/client";
import { PageHeader, EmptyState, fmtDate } from "@/components/common";
import { Plus, Edit, Trash2, Pause, Play, Zap, Loader2, Search, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm";

const TABS = [
  { key: "", label: "All" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "half_yearly", label: "Half-Yearly" },
  { key: "yearly", label: "Yearly" },
];

const RECURRENCE_OPTS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-Yearly" },
  { value: "yearly", label: "Yearly" },
  { value: "one_time", label: "One-Time" },
];

export default function RecurringTasks() {
  const [tab, setTab] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab) params.tab = tab;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/recurring-tasks", { params });
      setRows(r.data?.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab, search]); // eslint-disable-line

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };
  const displayed = useMemo(() => {
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const va = a[sortKey] ?? "", vb = b[sortKey] ?? "";
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);
  const SortTh = ({ label, k }) => (
    <th onClick={() => toggleSort(k)} className="cursor-pointer select-none group">
      <div className="flex items-center gap-1.5">{label}
        {sortKey === k ? (sortDir === "asc" ? <ChevronUp size={11} className="text-[#334E7B]"/> : <ChevronDown size={11} className="text-[#334E7B]"/>)
          : <ChevronsUpDown size={11} className="opacity-0 group-hover:opacity-50 transition-opacity"/>}
      </div>
    </th>
  );
  useEffect(() => {
    api.get("/users?limit=200").then(r => setUsers(r.data?.data || []));
    api.get("/masters/priorities").then(r => setPriorities(r.data?.data || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/recurring-tasks/${editing.id}`, form);
      else await api.post("/recurring-tasks", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.message || "Failed"); }
  };

  const action = async (r, act) => {
    try { await api.post(`/recurring-tasks/${r.id}/${act}`); toast.success("Done"); load(); }
    catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  const remove = async (r) => {
    if (!(await confirmDialog({ title: "Delete schedule", message: `Delete schedule "${r.title}"?`, confirmText: "Delete", danger: true }))) return;
    try { await api.delete(`/recurring-tasks/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  return (
    <div data-testid="recurring-page">
      <PageHeader title="Recurring Tasks" breadcrumbs={["Transactions", "Recurring"]}
        actions={<button onClick={() => { setEditing(null); setForm({ recurrence_type: "daily", default_sla_hours: 48, is_active: true }); setOpen(true); }} className="erp-btn-primary inline-flex items-center gap-2" data-testid="new-recurring-btn"><Plus size={14}/> New Schedule</button>}
      />

      {/* Search */}
      <div className="erp-card p-3 mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search schedules..." className="erp-input pl-8 text-sm" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12}/></button>}
        </div>
        <span className="text-xs text-slate-500">{rows.length} schedules</span>
      </div>

      <div className="border-b border-slate-200 mb-4 overflow-x-auto">
        <div className="flex space-x-6 text-sm font-medium">
          {TABS.map(t => (
            <button key={t.key || "all"} onClick={() => setTab(t.key)}
              className={`pb-2 whitespace-nowrap ${tab === t.key ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`recur-tab-${t.key || "all"}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="erp-card overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> :
         rows.length === 0 ? <EmptyState /> : (
          <table className="erp-table">
            <thead><tr>
              <SortTh label="Title" k="title"/>
              <SortTh label="Assignee" k="assigned_to_name"/>
              <SortTh label="Recurrence" k="recurrence_type"/>
              <SortTh label="Next Run" k="next_run_date"/>
              <SortTh label="Generated" k="occurrences_generated"/>
              <SortTh label="Status" k="is_active"/>
              <th></th>
            </tr></thead>
            <tbody>
              {displayed.map(r => (
                <tr key={r.id}>
                  <td className="font-medium">{r.title}</td>
                  <td>{r.assigned_to_name || "—"}</td>
                  <td><span className="badge-base bg-blue-50 text-blue-700 border-blue-200">{r.recurrence_type}</span></td>
                  <td className="text-xs">{fmtDate(r.next_run_date)}</td>
                  <td>{r.occurrences_generated || 0}</td>
                  <td>{r.is_active ? <span className="badge-base bg-emerald-50 text-emerald-700 border-emerald-200">Active</span> : <span className="badge-base bg-slate-100 text-slate-500 border-slate-200">Paused</span>}</td>
                  <td>
                    <div className="flex justify-end gap-1">
                      <button onClick={() => action(r, "run-now")} className="p-1.5 text-slate-600 hover:text-[#334E7B] hover:bg-blue-50 rounded-sm" title="Run now"><Zap size={14}/></button>
                      <button onClick={() => action(r, r.is_active ? "pause" : "resume")} className="p-1.5 hover:bg-slate-100 rounded-sm" title={r.is_active ? "Pause" : "Resume"}>
                        {r.is_active ? <Pause size={14}/> : <Play size={14}/>}
                      </button>
                      <button onClick={() => { setEditing(r); setForm(r); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded-sm"><Edit size={14}/></button>
                      <button onClick={() => remove(r)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <Dialog open onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl" aria-describedby={undefined}>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} Recurring Schedule</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Title *</label>
                <input value={form.title || ""} onChange={e => setForm({...form, title: e.target.value})} className="erp-input mt-1" required data-testid="recur-title-input"/>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Description</label>
                <textarea value={form.description || ""} onChange={e => setForm({...form, description: e.target.value})} className="erp-input mt-1" rows={2}/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Assignee *</label>
                  <div className="mt-1" data-testid="recur-assignee-input">
                    <SearchableSelect
                      options={users.map(u => ({ value: u.id, label: u.name }))}
                      value={form.assigned_to || ""}
                      onValueChange={v => setForm({...form, assigned_to: v})}
                      placeholder="Select user"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Recurrence *</label>
                  <select className="erp-input mt-1" value={form.recurrence_type || "daily"} onChange={e => setForm({...form, recurrence_type: e.target.value})} data-testid="recur-type-input">
                    {RECURRENCE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Priority</label>
                  <div className="mt-1">
                    <SearchableSelect
                      options={priorities.map(p => ({ value: p.id, label: p.name }))}
                      value={form.priority_id || ""}
                      onValueChange={v => setForm({...form, priority_id: v})}
                      placeholder="Select priority"
                      allowClear
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Default SLA (hrs)</label>
                  <input type="number" className="erp-input mt-1" value={form.default_sla_hours || 48} onChange={e => setForm({...form, default_sla_hours: Number(e.target.value)})}/>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Start Date</label>
                  <input type="date" className="erp-input mt-1" value={form.start_date || ""} onChange={e => setForm({...form, start_date: e.target.value})}/>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">End Date</label>
                  <input type="date" className="erp-input mt-1" value={form.end_date || ""} onChange={e => setForm({...form, end_date: e.target.value})}/>
                </div>
              </div>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.approval_required} onChange={e => setForm({...form, approval_required: e.target.checked})}/> Approval Required</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.return_confirmation_required} onChange={e => setForm({...form, return_confirmation_required: e.target.checked})}/> Return Confirmation</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.skip_holidays} onChange={e => setForm({...form, skip_holidays: e.target.checked})}/> Skip Holidays</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active !== false} onChange={e => setForm({...form, is_active: e.target.checked})}/> Active</label>
              </div>
              <DialogFooter>
                <button type="button" onClick={() => setOpen(false)} className="erp-btn-secondary">Cancel</button>
                <button type="submit" className="erp-btn-primary">{editing ? "Update" : "Create"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
